import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { Link2Off } from 'lucide-react';
import { Alert } from '@/components/alert';
import { sanitizeRouteParam } from '@/utils';
import { getApiErrorMessage } from '@/utils/ValidationErrors';
import portalService from '@/services/portal/portalService';
import { usePortal } from '@/portal/PortalProvider';
import { PortalAuthCard } from './PortalAuthCard';
import { PortalPasswordField } from './PortalPasswordField';
import { PASSWORD_HINT, confirmPasswordSchema, newPasswordSchema } from './passwordRules';

const schema = Yup.object().shape({
  password: newPasswordSchema,
  password_confirmation: confirmPasswordSchema,
});

/**
 * Where invite and reset links land: choose a password, then go straight in.
 * Each link works once.
 */
const PortalSetPassword = () => {
  const { token: rawToken } = useParams();
  const token = sanitizeRouteParam(rawToken);
  const navigate = useNavigate();
  const { setPasswordFromLink } = usePortal();
  const [link, setLink] = useState(null); // { name, email, purpose }
  const [deadLink, setDeadLink] = useState(null);

  useEffect(() => {
    let cancelled = false;
    portalService
      .checkPasswordToken(token)
      .then((data) => !cancelled && setLink(data))
      .catch((error) => !cancelled && setDeadLink(getApiErrorMessage(error, 'This link has expired or has already been used.')));
    return () => {
      cancelled = true;
    };
  }, [token]);

  const formik = useFormik({
    initialValues: { password: '', password_confirmation: '' },
    validationSchema: schema,
    onSubmit: async (values, { setStatus }) => {
      setStatus(null);
      try {
        await setPasswordFromLink(token, values.password, values.password_confirmation);
        navigate('/portal', { replace: true });
      } catch (error) {
        setStatus(getApiErrorMessage(error, 'The password could not be saved. Please try again.'));
      }
    },
  });

  if (deadLink) {
    return (
      <PortalAuthCard title="This link can't be used">
        <div className="flex flex-col items-center gap-4 text-center">
          <Link2Off size={30} className="text-gray-400" />
          <p className="text-2sm text-gray-700">{deadLink}</p>
          <p className="text-2xs text-gray-500">Each link works once, and only for a limited time. You can ask for a new one.</p>
          <Link to="/portal/forgot-password" className="btn btn-dark">Email me a new link</Link>
          <Link to="/portal/login" className="text-2sm link">Back to sign in</Link>
        </div>
      </PortalAuthCard>
    );
  }

  if (!link) {
    return <p className="text-center text-2sm text-gray-600 py-10">Loading…</p>;
  }

  const isInvite = link.purpose === 'invite';

  return (
    <PortalAuthCard
      title={isInvite ? `Welcome, ${link.name}` : 'Choose a new password'}
      subtitle={isInvite ? 'Choose a password for your client portal account.' : `For ${link.email}`}
    >
      <form className="flex flex-col gap-5" onSubmit={formik.handleSubmit} noValidate>
        {formik.status && <Alert variant="danger">{formik.status}</Alert>}

        {isInvite && (
          <div className="flex flex-col gap-1">
            <span className="form-label text-gray-900">Email</span>
            <span className="text-2sm text-gray-700">{link.email}</span>
          </div>
        )}

        <PortalPasswordField id="portal-new-password" label="Password" autoComplete="new-password" formik={formik} name="password" />
        <PortalPasswordField
          id="portal-confirm-password"
          label="Confirm password"
          autoComplete="new-password"
          formik={formik}
          name="password_confirmation"
        />
        <p className="text-2xs text-gray-500 -mt-2">{PASSWORD_HINT}</p>

        <button type="submit" className="btn btn-dark flex justify-center grow" disabled={formik.isSubmitting}>
          {formik.isSubmitting ? 'Saving…' : isInvite ? 'Create account' : 'Save password'}
        </button>
      </form>
    </PortalAuthCard>
  );
};

export { PortalSetPassword };
